import axios from "axios";
import { handleActions } from "redux-actions";
import { PaginatedQuery, PaginatedResponse } from "interfaces/paginated-query";
import Config from "config";
import { getHeaders } from "helpers/headers";
import { AnyAction } from "interfaces/action";
import { Dispatch } from "interfaces/dispatch";
import { FetchAllStoresOrFilesQueryOptions } from "interfaces/filterQuery";
import { Store } from "interfaces/store";
import { ThunkActionCreator } from "interfaces/thunk";
import { CREATE_PROJECT_REQUEST } from "state/create_project";
import queryString from "query-string";
import {SeverityLevel, ToastService} from "../services/toastService";

// Actions Types
const FETCH_STORES_BY_PROJECT_REQUEST = "FETCH_STORES_BY_PROJECT_REQUEST";
const FETCH_STORES_BY_PROJECT = "FETCH_STORES_BY_PROJECT";
const FETCH_STORES_BY_PROJECT_FAILED = "FETCH_STORES_BY_PROJECT_FAILED";

const FETCH_STORES_REQUEST = "FETCH_STORES_REQUEST";
const FETCH_STORES = "FETCH_STORES";
const FETCH_STORES_FAILED = "FETCH_STORES_FAILED";

const FETCH_USER_STORES_REQUEST = "FETCH_USER_STORES_REQUEST";
const FETCH_USER_STORES = "FETCH_USER_STORES";
const FETCH_USER_STORES_FAILED = "FETCH_USER_STORES_FAILED";

const DELETE_STORE_REQUEST = "DELETE_STORES_REQUEST";
const DELETE_STORE = "DELETE_STORES";
const DELETE_STORE_FAILED = "DELETE_STORES_FAILED";

const CREATE_STORE_REQUEST = "CREATE_STORE_REQUEST";
const CREATE_STORE = "CREATE_STORE";
const CREATE_STORE_FAILED = "CREATE_STORE_FAILED";

const UPDATE_STORE_REQUEST = "UPDATE_STORE_REQUEST";
const UPDATE_STORE = "UPDATE_STORE";
const UPDATE_STORE_FAILED = "UPDATE_STORE_FAILED";

const CLEAR_STORE = "CLEAR_STORE";

export {
  FETCH_STORES_BY_PROJECT,
  FETCH_STORES_BY_PROJECT_FAILED,
  FETCH_STORES_BY_PROJECT_REQUEST,
  FETCH_STORES,
  FETCH_STORES_REQUEST,
  FETCH_STORES_FAILED,
  FETCH_USER_STORES,
  FETCH_USER_STORES_REQUEST,
  FETCH_USER_STORES_FAILED,
  DELETE_STORE_REQUEST,
  DELETE_STORE,
  DELETE_STORE_FAILED,
  CREATE_STORE_REQUEST,
  CREATE_STORE,
  CREATE_STORE_FAILED,
  UPDATE_STORE_REQUEST,
  UPDATE_STORE,
  UPDATE_STORE_FAILED,
  CLEAR_STORE,
};

type FetchStoresByProjectResultAction =
  | { type: typeof FETCH_STORES_BY_PROJECT; payload: PaginatedResponse<Store> }
  | { type: typeof FETCH_STORES_BY_PROJECT_FAILED };

// Thunk Action Creator
const fetchStoresByProjectId: ThunkActionCreator<Promise<FetchStoresByProjectResultAction>> =
  (
    projectId: number,
    queryOptions: PaginatedQuery = { size: 100000000, page: 0, includeAggregate: false }
  ) =>
  (dispatch: Dispatch) => {
    dispatch({ type: FETCH_STORES_BY_PROJECT_REQUEST });

    // TODO: Update default queryOptions once the API endpoint supports requesting all records
    const url = `${Config.API_SERVICE_URL}/api/projects/${projectId}/stores?${queryString.stringify(
      queryOptions
    )}`;

    return axios
      .get(url, getHeaders())
      .then((response) => {
        const successAction: FetchStoresByProjectResultAction = {
          type: FETCH_STORES_BY_PROJECT,
          payload: response.data as PaginatedResponse<Store>,
        };
        dispatch(successAction);
        return successAction;
      })
      .catch((error: Error) => {
        const failedAction: FetchStoresByProjectResultAction = {
          type: FETCH_STORES_BY_PROJECT_FAILED,
        };
        dispatch(failedAction);
        return failedAction;
      });
  };

const fetchStores: ThunkActionCreator<Promise<void>> =
  (queryOptions: any = { size: 100, page: 0 }) =>
  (dispatch: Dispatch) => {
    dispatch({ type: FETCH_STORES_REQUEST });

    // Map chainId → chain (what the API expects), strip chainId from params
    const { chainId, ...rest } = queryOptions;
    const params: any = { ...rest };
    if (chainId !== undefined && chainId > -1) {
      params.chain = chainId;
    }

    const url = `${Config.API_SERVICE_URL}/api/stores?${queryString.stringify(params)}`;

    return axios
      .get(url, getHeaders())
      .then((response) => {
        dispatch({ type: FETCH_STORES, payload: response.data });
      })
      .catch((error: Error) => {
        dispatch({ type: FETCH_STORES_FAILED });
      });
  };

const fetchUserStores: ThunkActionCreator<Promise<PaginatedResponse<Store> | undefined>> =
  (queryOptions?: FetchAllStoresOrFilesQueryOptions) => (dispatch: Dispatch) => {
    dispatch({ type: FETCH_USER_STORES_REQUEST });

    let url = `${Config.API_SERVICE_URL}/api/stores/me`;

    if (queryOptions && Object.keys(queryOptions).length > 0) {
      url = `${url}?${queryString.stringify(queryOptions)}`;
    }

    return axios
      .get(url, getHeaders())
      .then((response) => {
        const data = response.data as PaginatedResponse<Store>;
        dispatch({ type: FETCH_USER_STORES, payload: data });
        // Return payload so callers can read fresh results in the same tick; `this.props`
        // from connect may still be stale until the next render.
        return data;
      })
      .catch((_) => {
        dispatch({ type: FETCH_USER_STORES_FAILED });
        return undefined;
      });
  };

export const deleteStoreById: ThunkActionCreator<Promise<void>> = (id) => (dispatch: Dispatch) => {
  dispatch({ type: DELETE_STORE_REQUEST });
  return axios
    .delete(`${Config.API_SERVICE_URL}/api/stores/${id}`, getHeaders())
    .then((_) => {
      dispatch({ type: DELETE_STORE });
    })
    .catch((error: Error) => {
      dispatch({ type: DELETE_STORE_FAILED });
    });
};

const createStoreInfo: ThunkActionCreator<Promise<void>> =
  (createStoreData) => (dispatch: Dispatch) => {
    dispatch({ type: CREATE_STORE_REQUEST });
    let url = `${Config.API_SERVICE_URL}/api/stores/saveStore`;
    return axios
      .post(url, createStoreData, getHeaders())
      .then((response) => {
        ToastService.getInstance().publishToast(
          {
            identifier: "success-operation",
            title: "Success",
            message: "Store updated successfully!",
          },
          SeverityLevel.SUCCESS,
          { autoClose: 3000 }
        );
        dispatch({ type: CREATE_STORE, payload: response.data });
      })
      .catch((e) => {
        dispatch({ type: CREATE_STORE_FAILED, payload: e.response.status });
      });
  };

const updateStoreInfo: ThunkActionCreator<Promise<void>> =
  (id: number, storeInfo) => (dispatch: Dispatch) => {
    dispatch({ type: UPDATE_STORE_REQUEST });
    let url = `${Config.API_SERVICE_URL}/api/stores/updateStore/${id}`;
    return axios
      .patch(url, storeInfo, getHeaders())
      .then((response) => {
        ToastService.getInstance().publishToast(
          {
            identifier: "success-operation",
            title: "Success",
            message: "Store updated successfully!",
          },
          SeverityLevel.SUCCESS,
          { autoClose: 3000 }
        );
        dispatch({ type: UPDATE_STORE, payload: response.data });
      })
      .catch((e) => {
        dispatch({ type: UPDATE_STORE_FAILED, payload: e.response.status });
      });
  };

const clearStore = () => (dispatch: Dispatch) => {
  dispatch({ type: CLEAR_STORE });
};

// Actions
const actions = {
  [CREATE_PROJECT_REQUEST]: () => ({ ...initialState }),
  [FETCH_STORES_BY_PROJECT_REQUEST]: (state: StoresState) => ({
    ...state,
    loading: true,
  }),
  [FETCH_STORES_BY_PROJECT]: (state: StoresState, action: AnyAction): StoresState => ({
    ...state,
    loading: false,
    content: action.payload,
  }),
  [FETCH_STORES_BY_PROJECT_FAILED]: (state: StoresState, action: AnyAction): StoresState => ({
    ...state,
    loading: false,
    error: true,
    content: initialState.content,
  }),
  [FETCH_STORES_REQUEST]: (state: StoresState) => ({
    ...state,
    loading: true,
  }),
  [FETCH_STORES]: (state: StoresState, action: AnyAction): StoresState => ({
    ...state,
    loading: false,
    content: action.payload,
    totalCount: action.payload.totalCount ?? 0,
    currentPage: action.payload.pageIndex ?? 0,
  }),
  [FETCH_STORES_FAILED]: (state: StoresState): StoresState => ({
    ...state,
    loading: false,
    error: true,
    content: initialState.content,
  }),
  [FETCH_USER_STORES_REQUEST]: (state: StoresState): StoresState => ({
    ...state,
    loading: true,
    error: false,
    content: initialState.content,
  }),
  [FETCH_USER_STORES]: (state: StoresState, action: AnyAction): StoresState => ({
    ...state,
    loading: false,
    content: action.payload,
  }),
  [FETCH_USER_STORES_FAILED]: (state: StoresState): StoresState => ({
    ...state,
    loading: false,
    error: true,
    content: initialState.content,
  }),
  [DELETE_STORE_REQUEST]: (state: StoresState): StoresState => ({
    ...state,
    loading: true,
  }),
  [DELETE_STORE]: (state: StoresState): StoresState => ({
    ...state,
    loading: false,
    content: initialState.content,
  }),
  [DELETE_STORE_FAILED]: (state: StoresState): StoresState => ({
    ...state,
    loading: false,
    error: true,
    content: initialState.content,
  }),

  [CREATE_STORE_REQUEST]: (state: StoresState) => ({
    ...state,
    loading: true,
  }),
  [CREATE_STORE]: (state: StoresState, action: AnyAction): StoresState => ({
    ...state,
    loading: false,
    saveData: action.payload,
    errorMessage: 0,
  }),
  [CREATE_STORE_FAILED]: (state: StoresState, action: AnyAction): StoresState => ({
    ...state,
    loading: false,
    error: true,
    saveData: false,
    errorMessage: action.payload,
  }),

  [UPDATE_STORE_REQUEST]: (state: StoresState) => ({
    ...state,
    loading: true,
  }),
  [UPDATE_STORE]: (state: StoresState, action: AnyAction): StoresState => ({
    ...state,
    loading: false,
    saveData: action.payload,
    errorMessage: 0,
  }),
  [UPDATE_STORE_FAILED]: (state: StoresState, action: AnyAction): StoresState => ({
    ...state,
    loading: false,
    error: true,
    saveData: false,
    errorMessage: action.payload,
  }),
  [CLEAR_STORE]: () => ({
    ...initialState,
  }),
};

// Reducer Interface
export interface StoresState {
  error: boolean;
  loading: boolean;
  content: PaginatedResponse<Store>;
  saveData: {};
  errorMessage: number;
  totalCount: number;
  currentPage: number;
}

// Initial State
const initialState: StoresState = {
  loading: false,
  error: false,
  content: { data: [] },
  saveData: {},
  errorMessage: 0,
  totalCount: 0,
  currentPage: 0,
};

export {
  fetchStoresByProjectId,
  fetchStores,
  fetchUserStores,
  createStoreInfo,
  updateStoreInfo,
  initialState,
  clearStore,
};

// Reducer
export default handleActions<StoresState, AnyAction>(actions, initialState);
